import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import PageTransition from "./components/PageTransition";
import { Toaster } from "sonner";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Services from "./components/Services";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import LogoSplash from "./components/LogoSplash";
import StrategyCall from "./components/StrategyCall";
import BookingDialog from "./components/BookingDialog";
import FuturisticFX from "./components/FuturisticFX";
import TrustStrip from "./components/TrustStrip";
import HomeFaq from "./components/HomeFaq";
import CustomCursor from "./components/CustomCursor";
import SmoothScroll from "./components/SmoothScroll";
import ScrollProgress from "./components/ScrollProgress";
import InteractiveFX from "./components/InteractiveFX";
import ConciergeChat from "./components/ConciergeChat";
import AnalyticsTracker from "./components/AnalyticsTracker";
import EngagementLayer from "./components/EngagementLayer";
import GhostLayer from "./components/GhostLayer";
import StickyCTA from "./components/StickyCTA";
import { PrivacyNotice } from "./components/PrivacyNotice";
import RouteMeta from "./components/RouteMeta";
import LatestInsights from "./components/LatestInsights";
import BuyerPaths from "./components/BuyerPaths";
import { initI18n } from "./lib/i18n";
import { IS_DAY } from "./lib/theme";

if (IS_DAY) document.documentElement.classList.add("theme-day");
import Admin from "./pages/Admin";
import GeoGate from "./components/GeoGate";
import { SOLUTIONS } from "./data/solutions";
import { REGIONS } from "./data/regions";

const PaymentsPage = lazy(() => import("./pages/PaymentsPage"));
const LegalCompliance = lazy(() => import("./pages/LegalCompliance"));
const TradingPlatformsPage = lazy(() => import("./pages/TradingPlatformsPage"));
const LpBridgePage = lazy(() => import("./pages/LpBridgePage"));
const CrmPage = lazy(() => import("./pages/CrmPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const SolutionPage = lazy(() => import("./pages/SolutionPage"));
const RegionPage = lazy(() => import("./pages/RegionPage"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const InsightsPage = lazy(() => import("./pages/InsightsPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));

/* Recovers from stale-deploy chunk errors (old HTML referencing removed JS) with a single reload */
class ChunkGuard extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) {
    const stale = /ChunkLoadError|Loading chunk|dynamically imported module/i.test(String(err?.message || err));
    if (stale && !sessionStorage.getItem("ahjin_chunk_reload")) {
      sessionStorage.setItem("ahjin_chunk_reload", "1");
      window.location.reload();
    }
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#050b1c] text-zinc-200 px-6 text-center" data-testid="app-error-fallback">
          <div>
            <div className="font-display text-2xl">A new version of the site was just published.</div>
            <button type="button" onClick={() => window.location.reload()} className="mt-5 rounded-full bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#1a1405]" data-testid="app-error-reload">
              Reload the page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Home = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
        else el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [location.hash, location.pathname]);

  return (
    <main className="relative bg-[#050b1c] text-zinc-100">
      <SmoothScroll />
      <CustomCursor />
      <ScrollProgress />
      <InteractiveFX />
      <FuturisticFX />
      <LogoSplash />
      <div className="relative z-10">
        <Header />
        <Hero />
        <TrustStrip />
        <BuyerPaths />
        <StrategyCall />
        <Services />
        <LuxDivider />
        <LatestInsights />
        <HomeFaq />
        <Contact />
        <Footer />
      </div>
    </main>
  );
};

const LuxDivider = () => (
  <div className="relative max-w-7xl mx-auto px-5 sm:px-10" aria-hidden="true">
    <div className="lux-divider">
      <motion.span
        className="line"
        initial={{ scaleX: 0, opacity: 0 }}
        whileInView={{ scaleX: 1, opacity: 1 }}
        viewport={{ once: true, amount: 0.9 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: "right center" }}
      />
      <span className="diamond" />
      <motion.span
        className="line"
        initial={{ scaleX: 0, opacity: 0 }}
        whileInView={{ scaleX: 1, opacity: 1 }}
        viewport={{ once: true, amount: 0.9 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: "left center" }}
      />
    </div>
  </div>
);

function App() {
  useEffect(() => {
    initI18n();
    if (localStorage.getItem("ahjin-light") === "1") {
      document.documentElement.classList.add("theme-light");
    }
  }, []);
  return (
    <GeoGate>
    <div className="App">
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "rgba(10,10,10,0.95)",
            border: "1px solid rgba(147,51,234,0.3)",
            color: "#F8F9FA",
            backdropFilter: "blur(12px)",
          },
        }}
      />
      <BrowserRouter>
        <AnalyticsTracker />
        <RouteMeta />
        <EngagementLayer />
        <StickyCTA />
        <PrivacyNotice />
        <BookingDialog />
        <ConciergeChat />
        <PageTransition />
        <GhostLayer />
        <ChunkGuard>
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/legal-compliance" element={<LegalCompliance />} />
          <Route path="/trading-platforms" element={<TradingPlatformsPage />} />
          <Route path="/lp-bridge" element={<LpBridgePage />} />
          <Route path="/crm" element={<CrmPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          {SOLUTIONS.map((s) => (
            <Route key={s.slug} path={`/${s.slug}`} element={<SolutionPage slug={s.slug} />} />
          ))}
          {REGIONS.map((r) => (
            <Route key={r.slug} path={`/${r.slug}`} element={<RegionPage slug={r.slug} />} />
          ))}
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/insights/:slug" element={<ArticlePage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </ChunkGuard>
      </BrowserRouter>
    </div>
    </GeoGate>
  );
}

export default App;
